import React from 'react';
const { useState } = React;
import { fmtDate, adminCall } from '../diagnostic/shared.jsx';
import { coachCall } from './shared.jsx';
import { navigate } from './ContactLink.jsx';
import { ROLES, t } from '../../diagnosticConfig.js';
import { defaultFormsForRole } from '../../serviceStart.js';
import { AirtableAPI } from '../../globals.js';
import { METHOD_INDUSTRIES, METHOD_TEAM_SIZES, METHOD_SALE_TYPES } from '../../methodLibraryConfig.js';
import { PRICING_CONFIG } from '../../pricing.js';
import { isTestName } from '../../testRecords.js';
import { STAGES, isClosedStage, newOpportunityFields, editOpportunityFields, contactLinkFields, contactFromPerson } from '../../personCommercial.js';
import ContactQuotes from '../quotes/ContactQuotes.jsx';

// The parts of the person card (Personas) that join every service: add a
// service, the commercial side, courses, pending, follow-ups and the history.
// They use the real records through the same functions as each module
// (coaching-admin, diagnostic-admin), so a change here is the same change
// there. The server checks every permission; these only show what came.

const card = { background: 'var(--globant-card)', border: '1px solid var(--globant-border)', borderRadius: 16, padding: 20 };
const muted = { fontSize: 13, color: 'var(--globant-muted)' };
const h3 = { margin: 0, fontSize: 17 };
const field = { font: 'inherit', fontSize: 14, padding: '8px 10px', border: '1px solid var(--globant-border)', borderRadius: 10, background: '#FFFFFF', color: 'var(--globant-text)' };
const KIND_LABEL = { coaching: 'Acompañamiento', diagnostic: 'Diagnóstico', learning: 'Curso', commercial: 'Comercial', general: 'General' };
const kindOf = (service) => String(service || 'general').split(':')[0];
const errText = (e) => (e.message === 'schema_pending' ? 'Falta la tabla Coaching Follow-ups en Airtable: ver docs/coaching/airtable-schema.md.' : e.message === 'fields_pending' ? 'Faltan campos en Airtable: ver docs/coaching/airtable-schema.md.' : e.message);

function Bar({ done, total }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }} role="progressbar" aria-label={`${done} de ${total}`} aria-valuemin={0} aria-valuemax={total} aria-valuenow={done}>
      <div style={{ flex: 1, height: 8, borderRadius: 4, background: '#EEEAE2', overflow: 'hidden' }}><div style={{ width: `${pct}%`, height: '100%', background: 'var(--globant-green)' }} /></div>
      <span style={muted}>{done}/{total}</span>
    </div>
  );
}

/**
 * Diagnóstico from the card, in one flow: the company (one of their contact's,
 * any other, or a new one, created through the commercial path), then an open
 * diagnostic of it or a new one, the role, and optionally the client's data that
 * P3, P5 and System Design need later. Everything is the same diagnostic-admin.
 */
function DiagnosticFlow({ v, tables, onDone }) {
  const own = v.commercial?.accounts || [];
  const [account, setAccount] = useState(own.length === 1 ? own[0] : null);
  const [q, setQ] = useState('');
  const [found, setFound] = useState(null);
  const [newName, setNewName] = useState('');
  const [diags, setDiags] = useState(null);
  const [mode, setMode] = useState('existing');
  const [pick, setPick] = useState('');
  const [form, setForm] = useState({ name: '', formsDueAt: '', isTest: false, role: '', industry: '', teamSize: '', saleType: '', country: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const takenDiag = new Set((v.diagnostics || []).map(d => d.diagnosticId));
  const choose = async (a) => {
    setAccount(a); setMsg(''); setDiags(null); setPick('');
    // A test person or company ("PRUEBA — …"): the diagnostic is created as a test one, out of every metric
    setForm(f => ({ ...f, name: `${a.name} — Diagnóstico comercial`, isTest: f.isTest || isTestName(a.name) || isTestName(v.person.name) }));
    try {
      const r = await adminCall({ action: 'list', accountId: a.id });
      const open = r.diagnostics.filter(d => !['closed', 'delivered'].includes(d.status));
      setDiags(open); setMode(open.length ? 'existing' : 'new');
    } catch (e) { setMsg(e.message); setDiags([]); setMode('new'); }
  };
  React.useEffect(() => { if (account && !diags) choose(account); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const search = async () => { setMsg(''); try { setFound((await adminCall({ action: 'accountSearch', q })).accounts); } catch (e) { setMsg(e.message); } };
  const createAccount = async () => {
    if (!newName.trim() || !window.confirm(`¿Crear la empresa "${newName.trim()}"? Se crea en Comercial, como cualquier empresa.`)) return;
    setBusy(true); setMsg('');
    try { const rec = await new AirtableAPI().createRecord(tables.accounts, { 'Account Name': newName.trim() }); await choose({ id: rec.id, name: newName.trim() }); setNewName(''); }
    catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true); setMsg('');
    try {
      let diagnosticId = pick;
      if (mode === 'new') {
        const r = await adminCall({ action: 'create', accountId: account.id, name: form.name.trim(), isTest: form.isTest, ...(form.formsDueAt ? { formsDueAt: form.formsDueAt } : {}) });
        diagnosticId = r.diagnostic.id;
        const ctx = Object.fromEntries(['industry', 'teamSize', 'saleType', 'country'].filter(k => form[k]).map(k => [k, form[k]]));
        if (Object.keys(ctx).length) await adminCall({ action: 'saveClientContext', diagnosticId, ...ctx });
      }
      await adminCall({ action: 'addParticipant', diagnosticId, personId: v.person.id, role: form.role, forms: defaultFormsForRole(form.role) });
      onDone(mode === 'new' ? 'Diagnóstico creado (en preparación) y la persona agregada.' : 'Agregada al diagnóstico.');
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };
  const ready = account && form.role && (mode === 'existing' ? !!pick : !!form.name.trim());
  return (
    <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>1 · Empresa</div>
        {account ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}><b>{account.name}</b><button type="button" className="action-btn" style={{ fontSize: 12 }} onClick={() => { setAccount(null); setDiags(null); }}>Cambiar</button></div>
        ) : <div style={{ display: 'grid', gap: 6 }}>
          {own.map(a => <button key={a.id} type="button" className="action-btn" onClick={() => choose(a)}>{a.name} (de su contacto)</button>)}
          <div style={{ display: 'flex', gap: 6 }}>
            <input style={{ ...field, flex: 1 }} value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && search()} placeholder="Buscar otra empresa" aria-label="Buscar empresa" />
            <button type="button" className="action-btn" onClick={search}>Buscar</button>
          </div>
          {found && (found.length ? found.map(a => <button key={a.id} type="button" className="action-btn" style={{ textAlign: 'left' }} onClick={() => choose(a)}>{a.name}</button>) : <span style={muted}>Ninguna con ese nombre.</span>)}
          <div style={{ display: 'flex', gap: 6 }}>
            <input style={{ ...field, flex: 1 }} value={newName} onChange={e => setNewName(e.target.value)} placeholder="O crear una empresa nueva" aria-label="Nueva empresa" />
            <button type="button" className="action-btn" disabled={busy || !newName.trim() || !tables.accounts} onClick={createAccount}>Crear</button>
          </div>
        </div>}
      </div>
      {account && diags && <>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>2 · Diagnóstico</div>
          <div role="radiogroup" aria-label="Diagnóstico" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
            <button type="button" role="radio" aria-checked={mode === 'existing'} disabled={!diags.length} className={`action-btn ${mode === 'existing' ? 'btn-primary' : ''}`} onClick={() => setMode('existing')}>Sumar a uno abierto ({diags.length})</button>
            <button type="button" role="radio" aria-checked={mode === 'new'} className={`action-btn ${mode === 'new' ? 'btn-primary' : ''}`} onClick={() => setMode('new')}>Crear uno nuevo</button>
          </div>
          {mode === 'existing' ? (
            <select style={field} value={pick} onChange={e => setPick(e.target.value)} aria-label="Diagnóstico abierto">
              <option value="">Elige…</option>
              {diags.map(d => <option key={d.id} value={d.id} disabled={takenDiag.has(d.id)}>{d.name}{d.isTest ? ' (prueba)' : ''}{takenDiag.has(d.id) ? ' · ya participa' : ''}</option>)}
            </select>
          ) : (
            <div style={{ display: 'grid', gap: 6 }}>
              <input style={field} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} aria-label="Nombre del diagnóstico" />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <label style={muted}>Plazo de formularios <input type="date" style={field} value={form.formsDueAt} onChange={e => setForm({ ...form, formsDueAt: e.target.value })} /></label>
                <label style={{ ...muted, display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={form.isTest} onChange={e => setForm({ ...form, isTest: e.target.checked })} /> De prueba</label>
              </div>
              <details>
                <summary style={{ cursor: 'pointer', fontSize: 13 }}>Datos del cliente (los necesitan P3, P5 y System Design; puedes completarlos después)</summary>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                  <select style={field} value={form.industry} onChange={e => setForm({ ...form, industry: e.target.value })} aria-label="Industria"><option value="">Industria…</option>{METHOD_INDUSTRIES.map(x => <option key={x.key} value={x.key}>{x.label}</option>)}</select>
                  <select style={field} value={form.teamSize} onChange={e => setForm({ ...form, teamSize: e.target.value })} aria-label="Tamaño del equipo"><option value="">Tamaño del equipo…</option>{METHOD_TEAM_SIZES.map(x => <option key={x.key} value={x.key}>{x.label}</option>)}</select>
                  <select style={field} value={form.saleType} onChange={e => setForm({ ...form, saleType: e.target.value })} aria-label="Tipo de venta"><option value="">Tipo de venta…</option>{METHOD_SALE_TYPES.map(x => <option key={x.key} value={x.key}>{x.label}</option>)}</select>
                  <select style={field} value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} aria-label="País"><option value="">País…</option>{Object.entries(PRICING_CONFIG.countries).map(([k, c]) => <option key={k} value={k}>{c.label}</option>)}</select>
                </div>
              </details>
            </div>
          )}
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>3 · Su rol</div>
          <select style={field} value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} aria-label="Rol">
            <option value="">Rol en el diagnóstico…</option>
            {Object.entries(ROLES).map(([k, r]) => <option key={k} value={k}>{t(r.label) || k}</option>)}
          </select>
        </div>
        <div><button type="button" className="action-btn btn-primary" disabled={busy || !ready} onClick={save}>{mode === 'new' ? 'Crear diagnóstico y agregarla' : 'Agregar al diagnóstico'}</button></div>
      </>}
      {msg && <div style={{ ...muted, color: '#9A6212' }} role="status">{msg}</div>}
    </div>
  );
}

/** "Añadir": diagnóstico, comercial, acompañamiento, curso y seguimiento, each through its own module. */
export function AddMenu({ v, onDone, onFollowUp }) {
  const [open, setOpen] = useState(null);
  const [opts, setOpts] = useState(null);
  const [choice, setChoice] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const p = v.person;
  const start = async (kind) => {
    setOpen(kind); setMsg(''); setChoice({}); setOpts(null);
    try {
      if (kind === 'coaching') setOpts(await coachCall({ action: 'overview' }));
      if (kind === 'course') setOpts(await coachCall({ action: 'learningOverview' }));
    } catch (e) { setMsg(errText(e)); }
  };
  const run = async (fn, done) => {
    setBusy(true); setMsg('');
    try { await fn(); setOpen(null); setMsg(done); onDone(); } catch (e) { setMsg(errText(e)); } finally { setBusy(false); }
  };
  const taken = new Set(v.participations.filter(x => x.memberStatus !== 'left').map(x => x.process.id));
  const ownCourses = new Set((v.learning || []).map(e => e.courseId));
  const btn = (kind, text) => <button type="button" className={`action-btn ${open === kind ? 'btn-primary' : ''}`} onClick={() => (kind === 'followup' ? onFollowUp() : start(kind))}>{text}</button>;
  return (
    <section style={card} aria-labelledby="add-title">
      <h3 id="add-title" style={h3}>Añadir</h3>
      <p style={{ ...muted, margin: '2px 0 10px' }}>Cada servicio se crea en su módulo, con sus permisos. Su link personal no cambia.</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {btn('diagnostic', 'Diagnóstico')}{btn('commercial', 'Comercial')}{btn('coaching', 'Acompañamiento')}{btn('course', 'Curso')}{btn('followup', 'Nota o avance')}
      </div>
      {open === 'diagnostic' && <DiagnosticFlow v={v} tables={v.tables || {}} onDone={(m) => { setOpen(null); setMsg(m); onDone(); }} />}
      {open === 'commercial' && (
        <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
          {p.stakeholderId
            ? <><span style={muted}>Las oportunidades y propuestas se crean en Comercial, desde su contacto. Aparecen acá solas.</span>
              <button type="button" className="action-btn btn-primary" onClick={() => navigate({ page: 'contacts', contactId: p.stakeholderId })}>Abrir su contacto</button></>
            : <span style={muted}>Primero vincula su contacto comercial (Editar ficha → contacto). Nada se une solo por el email.</span>}
        </div>
      )}
      {open === 'coaching' && (
        <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
          {!opts ? <span style={muted}>Cargando…</span> : <>
            <div role="group" aria-label="Modalidad" style={{ display: 'flex', gap: 6 }}>
              {[['individual', 'Individual (nuevo)'], ['team', 'Sumar a un equipo']].map(([k, l]) => <button key={k} type="button" className={`action-btn ${choice.mode === k ? 'btn-primary' : ''}`} onClick={() => setChoice({ mode: k })}>{l}</button>)}
            </div>
            {choice.mode === 'individual' && <>
              <select style={field} value={choice.programId || ''} onChange={e => setChoice({ ...choice, programId: e.target.value })} aria-label="Programa">
                <option value="">Elige un programa…</option>
                {opts.programs.filter(x => x.status === 'active' && x.modality === 'individual').map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
              <button type="button" className="action-btn btn-primary" disabled={busy || !choice.programId}
                onClick={() => run(() => coachCall({ action: 'createProcess', programId: choice.programId, personIds: [p.id] }), 'Acompañamiento creado (invitado, sin iniciar).')}>Crear acompañamiento</button>
            </>}
            {choice.mode === 'team' && <>
              <select style={field} value={choice.processId || ''} onChange={e => setChoice({ ...choice, processId: e.target.value })} aria-label="Equipo">
                <option value="">Elige un equipo…</option>
                {opts.processes.filter(x => x.modality === 'team' && x.status !== 'closed' && !taken.has(x.id)).map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
              <span style={muted}>Para un equipo nuevo, créalo en Acompañamiento → Nuevo proceso.</span>
              <button type="button" className="action-btn btn-primary" disabled={busy || !choice.processId}
                onClick={() => run(() => coachCall({ action: 'addMember', processId: choice.processId, personId: p.id }), 'Sumada al equipo.')}>Sumar al equipo</button>
            </>}
          </>}
        </div>
      )}
      {open === 'course' && (
        <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
          {!opts ? <span style={muted}>Cargando…</span> : <>
            <select style={field} value={choice.courseId || ''} onChange={e => setChoice({ courseId: e.target.value })} aria-label="Curso">
              <option value="">Elige un curso publicado…</option>
              {opts.courses.filter(c => c.status === 'active').map(c => <option key={c.id} value={c.id} disabled={ownCourses.has(c.id)}>{c.name}{ownCourses.has(c.id) ? ' (ya lo tiene)' : ''}</option>)}
            </select>
            <button type="button" className="action-btn btn-primary" disabled={busy || !choice.courseId}
              onClick={() => run(() => coachCall({ action: 'learningAssign', courseId: choice.courseId, personIds: [p.id] }), 'Curso asignado.')}>Asignar curso</button>
          </>}
        </div>
      )}
      {msg && <div style={{ ...muted, marginTop: 8 }} role="status">{msg}</div>}
    </section>
  );
}

/** A company to choose: search the existing ones or create a new one (through the commercial path). */
function CompanyPicker({ tables, disabled, onPick }) {
  const [q, setQ] = useState('');
  const [found, setFound] = useState(null);
  const [msg, setMsg] = useState('');
  const search = async () => { setMsg(''); try { setFound((await adminCall({ action: 'accountSearch', q })).accounts); } catch (e) { setMsg(e.message); } };
  const create = async () => {
    const name = q.trim();
    if (!name || !window.confirm(`¿Crear la empresa "${name}"? Se crea en Comercial, como cualquier empresa.`)) return;
    try { const rec = await new AirtableAPI().createRecord(tables.accounts, { 'Account Name': name }); onPick({ id: rec.id, name }); } catch (e) { setMsg(e.message); }
  };
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <div style={{ display: 'flex', gap: 6 }}>
        <input style={{ ...field, flex: 1 }} value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && search()} placeholder="Buscar o crear la empresa" aria-label="Empresa" />
        <button type="button" className="action-btn" disabled={disabled} onClick={search}>Buscar</button>
        <button type="button" className="action-btn" disabled={disabled || !q.trim() || !tables.accounts} onClick={create}>Crear</button>
      </div>
      {found && (found.length ? found.map(a => <button key={a.id} type="button" className="action-btn" style={{ textAlign: 'left' }} disabled={disabled} onClick={() => onPick(a)}>{a.name}</button>) : <span style={muted}>Ninguna con ese nombre: puedes crearla.</span>)}
      {msg && <span style={{ ...muted, color: '#9A6212' }}>{msg}</span>}
    </div>
  );
}

/** One opportunity of the person: its stage, next step and value, edited through the commercial path. */
function OpportunityRow({ o, tables, onSaved }) {
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const save = async () => {
    setBusy(true); setMsg('');
    try {
      // Only what changed: an existing stage outside Oike's list, or any other field, stays as it is
      const fields = editOpportunityFields(edit, o);
      if (Object.keys(fields).length) await new AirtableAPI().updateRecord(tables.opportunities, o.id, fields);
      setEdit(null); onSaved();
    }
    catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };
  return (
    <div style={{ border: '1px solid var(--globant-border)', borderRadius: 12, padding: 12, display: 'grid', gap: 6, opacity: o.closed ? 0.75 : 1 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <b style={{ flex: '1 1 200px' }}>{o.name}</b>
        <span style={muted}>{o.stage || 'Sin etapa'}{o.value != null ? ` · ${o.value}` : ''}{o.closeDate ? ` · cierre ${fmtDate(o.closeDate)}` : ''}</span>
        {!edit && <button type="button" className="action-btn" style={{ fontSize: 12 }} onClick={() => setEdit({ name: o.name, stage: o.stage || '', nextStep: o.nextStep, value: o.value ?? '', closeDate: o.closeDate ? String(o.closeDate).slice(0, 10) : '', reason: o.reason })}>Editar</button>}
      </div>
      {o.nextStep && !edit && <div style={{ fontSize: 14 }}><span style={muted}>Próximo paso: </span>{o.nextStep}</div>}
      <div style={muted}>{o.via === 'contact' ? 'Vinculada a su contacto' : 'Creada desde una solicitud suya'}</div>
      {edit && (
        <div style={{ display: 'grid', gap: 6 }}>
          <input style={field} value={edit.name} onChange={e => setEdit({ ...edit, name: e.target.value })} aria-label="Nombre" />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <select style={field} value={edit.stage} onChange={e => setEdit({ ...edit, stage: e.target.value })} aria-label="Etapa">
              {/* Stage is a text in Airtable: a value outside Oike's list is shown and kept unless changed */}
              {edit.stage && !STAGES.includes(edit.stage) && <option value={edit.stage}>{edit.stage} (actual)</option>}
              {!edit.stage && <option value="">Sin etapa</option>}
              {STAGES.map(x => <option key={x} value={x}>{x}</option>)}
            </select>
            <input style={{ ...field, width: 130 }} type="number" min={0} value={edit.value} onChange={e => setEdit({ ...edit, value: e.target.value })} aria-label="Valor" placeholder="Valor" />
            <input style={field} type="date" value={edit.closeDate} onChange={e => setEdit({ ...edit, closeDate: e.target.value })} aria-label="Fecha de cierre" />
          </div>
          <input style={field} value={edit.nextStep} onChange={e => setEdit({ ...edit, nextStep: e.target.value })} aria-label="Próximo paso" placeholder="Próximo paso" />
          <textarea style={{ ...field, minHeight: 60 }} value={edit.reason} onChange={e => setEdit({ ...edit, reason: e.target.value })} aria-label="Descripción" placeholder="Descripción" />
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" className="action-btn btn-primary" disabled={busy} onClick={save}>Guardar</button>
            <button type="button" className="action-btn" onClick={() => setEdit(null)}>Cancelar</button>
          </div>
        </div>
      )}
      {msg && <div style={{ ...muted, color: '#9A6212' }}>{msg}</div>}
    </div>
  );
}

/**
 * The commercial side: proposals addressed to their contact (read), their own
 * opportunities (create and manage, through Comercial's own path) and, apart, the
 * open opportunities of their company that are not attributed to anyone.
 */
// withContactParts: false inside the unified client file, where the contact's own card (quotes, payments) is already shown
export function CommercialBlock({ v, onChanged, withContactParts = true }) {
  const com = v.commercial;
  const p = v.person;
  const tables = v.tables || {};
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const accounts = com?.accounts || [];
  const ready = !!com?.contactFieldReady;
  const run = async (fn, done) => {
    setBusy(true); setMsg('');
    try { await fn(); setMsg(done); onChanged(); } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };
  const [cq, setCq] = useState('');
  const [contacts, setContacts] = useState(null);
  const searchContacts = async () => { setMsg(''); try { setContacts((await coachCall({ action: 'contactSearch', q: cq })).contacts.slice(0, 10)); } catch (e) { setMsg(e.message); } };
  const linkExisting = (c) => window.confirm(`¿Vincular a ${p.name} con el contacto ${c.name}${c.email ? ` (${c.email})` : ''}? Queda en el historial del vínculo.`)
    && run(() => coachCall({ action: 'linkContact', personId: p.id, stakeholderId: c.id, note: 'Desde la ficha (elegido por la coach)' }), 'Contacto vinculado.');
  const setCompany = (a) => window.confirm(`¿${a.name} es la empresa de su contacto?`)
    && run(() => new AirtableAPI().updateRecord(tables.stakeholders, p.stakeholderId, { 'Account': [a.id] }), 'Empresa vinculada a su contacto.');
  const createContact = () => run(async () => {
    const found = p.email ? (await coachCall({ action: 'contactSearch', q: p.email })).contacts.filter(c => c.email === String(p.email).toLowerCase()) : [];
    if (found.length) {
      if (!window.confirm(`Ya hay un contacto con su email: ${found[0].name}. ¿Vincularlo a esta ficha?`)) throw new Error('Nada cambió.');
      await coachCall({ action: 'linkContact', personId: p.id, stakeholderId: found[0].id, note: 'Desde la ficha (mismo email, confirmado)' });
      return;
    }
    if (!window.confirm(`¿Crear un contacto comercial para ${p.name} y vincularlo a esta ficha?`)) throw new Error('Nada cambió.');
    const rec = await new AirtableAPI().createRecord(tables.stakeholders, contactFromPerson(p));
    await coachCall({ action: 'linkContact', personId: p.id, stakeholderId: rec.id, note: 'Contacto creado desde la ficha' });
  }, 'Contacto vinculado.');
  const create = () => run(async () => {
    await new AirtableAPI().createRecord(tables.opportunities, newOpportunityFields(form, { stakeholderId: p.stakeholderId, accountId: form.accountId || null }));
    setForm(null);
  }, 'Oportunidad creada y vinculada a su contacto.');
  // Only an opportunity without any contact; the server refuses to overwrite an existing one (contact_already_set)
  const attribute = (o) => window.confirm(`¿"${o.name}" es de ${p.name}? Pasa a sus oportunidades.`) && run(() => new AirtableAPI().updateRecord(tables.opportunities, o.id, contactLinkFields(p.stakeholderId)), 'Vinculada a su contacto.');
  return (
    <section style={card} aria-labelledby="com-title">
      <h3 id="com-title" style={h3}>Comercial</h3>
      {!p.stakeholderId ? <>
        <p style={muted}>Sin contacto comercial vinculado. Las oportunidades y propuestas son de un contacto: búscalo y vincúlalo, o créalo con sus datos. Nada se une solo por el email o el nombre.</p>
        <div style={{ display: 'flex', gap: 6 }}>
          <input style={{ ...field, flex: 1 }} value={cq} onChange={e => setCq(e.target.value)} onKeyDown={e => e.key === 'Enter' && searchContacts()} placeholder="Buscar un contacto por nombre o email" aria-label="Buscar contacto" />
          <button type="button" className="action-btn" onClick={searchContacts}>Buscar</button>
        </div>
        {contacts && (contacts.length ? contacts.map(c => (
          <div key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, padding: '4px 0' }}>
            <span style={{ flex: 1 }}>{c.name}<span style={muted}>{c.email ? ` · ${c.email}` : ''}{c.role ? ` · ${c.role}` : ''}</span></span>
            <button type="button" className="action-btn" style={{ fontSize: 12 }} disabled={busy} onClick={() => linkExisting(c)}>Vincular</button>
          </div>
        )) : <span style={muted}>Ningún contacto con esa búsqueda.</span>)}
        <button type="button" className="action-btn btn-primary" style={{ marginTop: 8 }} disabled={busy || !tables.stakeholders} onClick={createContact}>Crear su contacto con sus datos</button>
      </> : <>
        <div style={{ fontSize: 13, fontWeight: 600, margin: '8px 0 4px' }}>Empresa de su contacto</div>
        {accounts.length ? <div style={{ fontSize: 14 }}>{accounts.map(a => a.name).join(', ')}</div> : <CompanyPicker tables={tables} disabled={busy} onPick={(a) => setCompany(a)} />}
        <div style={{ fontSize: 13, fontWeight: 600, margin: '8px 0 6px' }}>Sus oportunidades</div>
        {com?.unavailable?.includes('opportunities') ? <div style={muted}>No se pudieron leer las oportunidades.</div>
          : !com?.opportunities?.personal?.length ? <div style={muted}>Ninguna vinculada a esta persona.</div>
          : <div style={{ display: 'grid', gap: 8 }}>{com.opportunities.personal.map(o => <OpportunityRow key={o.id} o={o} tables={tables} onSaved={onChanged} />)}</div>}
        {com?.opportunities?.ambiguous?.length > 0 && (
          <div style={{ marginTop: 8, padding: 10, borderRadius: 10, background: '#FBF0DC', color: '#6B4510', fontSize: 13 }}>
            <b>Para revisar en Comercial</b> · no se atribuyen a esta persona ni se cambian desde aquí:
            {com.opportunities.ambiguous.map(o => <div key={o.id}>{o.name}{o.contactCount > 1 ? ` · vinculada a ${o.contactCount} contactos` : ' · creada desde una solicitud suya, pero con el contacto de otra persona'}</div>)}
          </div>
        )}
        {!ready && <p style={{ ...muted, color: '#9A6212' }}>Para crear o vincular oportunidades a la persona falta el campo Contact en Oportunidades (pedido para Omni). Mientras, solo se ven las creadas desde una solicitud suya.</p>}
        {ready && !form && <button type="button" className="action-btn btn-primary" style={{ marginTop: 8 }} onClick={() => setForm({ name: `${p.name} · `, stage: 'Discovery', nextStep: '', value: '', closeDate: '', accountId: accounts.length === 1 ? accounts[0].id : '' })}>Nueva oportunidad</button>}
        {form && (
          <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
            <input style={field} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} aria-label="Nombre de la oportunidad" />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <select style={field} value={form.stage} onChange={e => setForm({ ...form, stage: e.target.value })} aria-label="Etapa">{STAGES.filter(x => !isClosedStage(x)).map(x => <option key={x} value={x}>{x}</option>)}</select>
              <select style={field} value={form.accountId} onChange={e => setForm({ ...form, accountId: e.target.value })} aria-label="Empresa">
                <option value="">Sin empresa (individual)</option>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <input style={{ ...field, width: 130 }} type="number" min={0} value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} aria-label="Valor" placeholder="Valor" />
            </div>
            <input style={field} value={form.nextStep} onChange={e => setForm({ ...form, nextStep: e.target.value })} aria-label="Próximo paso" placeholder="Próximo paso" />
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" className="action-btn btn-primary" disabled={busy} onClick={create}>Crear oportunidad</button>
              <button type="button" className="action-btn" onClick={() => setForm(null)}>Cancelar</button>
            </div>
          </div>
        )}
        {withContactParts && <div style={{ margin: '14px 0 4px' }}>
          {/* The same block as in Contactos: her quotes by contact id, and a new one to her or to her company, chosen explicitly */}
          <ContactQuotes contact={{ id: p.stakeholderId, name: p.name }} accounts={accounts.map(a => ({ id: a.id, name: a.name }))} tableId={tables.diagnosticIntake} onChanged={onChanged} />
        </div>}
        <div style={{ fontSize: 13, fontWeight: 600, margin: '10px 0 4px' }}>Enlaces de las propuestas publicadas</div>
        {com?.unavailable?.includes('proposals') ? <div style={muted}>No se pudieron leer las propuestas.</div>
          : !com?.proposals?.some(q => q.publishedAt) ? <div style={muted}>Ninguna publicada todavía.</div>
          : com.proposals.filter(q => q.publishedAt).map(q => (
            <div key={q.id} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--globant-border)' }}>
              <b style={{ flex: '1 1 200px' }}>{q.title}</b>
              <span style={muted}>{q.statusLabel}{q.publishedAt ? ` · publicada ${fmtDate(q.publishedAt)}` : ' · sin publicar'}</span>
              {q.link && <a className="action-btn" href={q.link} target="_blank" rel="noopener noreferrer">Ver</a>}
            </div>
          ))}
        <details style={{ marginTop: 14 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>De su empresa, no atribuidas a ella ({com?.opportunities?.company?.length || 0})</summary>
          <p style={{ ...muted, margin: '4px 0 6px' }}>Oportunidades abiertas de {accounts.map(a => a.name).join(', ') || 'su empresa'} sin un contacto propio. No son de esta persona salvo que lo indiques.</p>
          {(com?.opportunities?.company || []).map(o => (
            <div key={o.id} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: 14, padding: '4px 0' }}>
              <span style={{ flex: '1 1 200px' }}>{o.name}<span style={muted}>{o.stage ? ` · ${o.stage}` : ''}</span></span>
              {ready && <button type="button" className="action-btn" style={{ fontSize: 12 }} disabled={busy} onClick={() => attribute(o)}>Es de esta persona</button>}
            </div>
          ))}
        </details>
        {withContactParts && <button type="button" className="action-btn" style={{ marginTop: 10 }} onClick={() => navigate({ page: 'contacts', contactId: p.stakeholderId })}>Abrir su contacto</button>}
      </>}
      {msg && <div style={{ ...muted, marginTop: 8 }} role="status">{msg}</div>}
    </section>
  );
}

export function LearningBlock({ v }) {
  if (!v.learning?.length) return null;
  return (
    <section style={card} aria-labelledby="lrn-title">
      <h3 id="lrn-title" style={h3}>Cursos</h3>
      <p style={{ ...muted, margin: '2px 0 10px' }}>Progreso: lecciones que marcó como vistas y trabajos completados después de tu revisión. Sin puntajes.</p>
      <div style={{ display: 'grid', gap: 10 }}>
        {v.learning.map(e => (
          <div key={e.id} style={{ border: '1px solid var(--globant-border)', borderRadius: 14, padding: 14, display: 'grid', gap: 6 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'space-between' }}>
              <b>{e.courseName}</b>
              <span style={muted}>v{e.version}{e.latestVersion > e.version ? ` · hay v${e.latestVersion}` : ''}{e.waiting ? ` · ${e.waiting} para revisar` : ''}</span>
            </div>
            <Bar done={e.done} total={e.total} />
            <span style={muted}>Asignado {fmtDate(e.assignedAt)}{e.lastActivityAt ? ` · última actividad ${fmtDate(e.lastActivityAt, true)}` : ' · todavía no empezó'}</span>
          </div>
        ))}
      </div>
      <p style={{ ...muted, marginTop: 8 }}>Las entregas y devoluciones se revisan en Acompañamiento → Mi aprendizaje.</p>
    </section>
  );
}

export function PendingBlock({ v }) {
  const label = (s) => v.services.find(x => x.key === s)?.label || KIND_LABEL[kindOf(s)];
  const WHO = { coach: 'Te toca', person: 'Le toca', both: 'Agendado' };
  return (
    <section style={card} aria-labelledby="pend-title">
      <h3 id="pend-title" style={h3}>Pendientes y próximos pasos</h3>
      {!v.pending.length ? <p style={muted}>Nada pendiente.</p> : (
        <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
          {v.pending.map((x, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline', fontSize: 14 }}>
              <span style={{ fontSize: 12, fontWeight: 600, padding: '1px 8px', borderRadius: 999, background: x.who === 'coach' ? 'var(--teal-dim)' : '#F1EFEA' }}>{WHO[x.who] || ''}</span>
              <b>{x.title}</b>
              <span style={muted}>{label(x.service)}{x.when ? ` · ${fmtDate(x.when, true)}` : ''}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/** Follow-ups by service (diagnóstico, comercial, acompañamiento, curso, general): internal (only the coach) or shared (in their space). */
export function FollowUpForm({ v, onDone, formRef }) {
  const [service, setService] = useState('general');
  const [mode, setMode] = useState('internal');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const save = async () => {
    if (mode === 'shared' && !window.confirm(`${v.person.name.split(' ')[0]} va a ver este avance en su espacio. ¿Compartirlo?`)) return;
    setBusy(true); setMsg('');
    try {
      await coachCall({ action: 'saveFollowUp', personId: v.person.id, service, text, visibility: mode });
      setText(''); setMsg(mode === 'shared' ? 'Avance compartido: ya aparece en su espacio.' : 'Nota interna guardada. Solo la ves tú.');
      onDone();
    } catch (e) { setMsg(errText(e)); } finally { setBusy(false); }
  };
  return (
    <section style={card} aria-labelledby="fu-title" ref={formRef}>
      <h3 id="fu-title" style={h3}>Registrar una nota interna o un avance compartido</h3>
      {!v.followUpsReady && <p style={{ ...muted, color: '#9A6212' }}>Falta la tabla Coaching Follow-ups (pedido para Omni o migración): hasta entonces no se puede guardar.</p>}
      <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
        <select style={field} value={service} onChange={e => setService(e.target.value)} aria-label="Servicio">
          {v.services.map(s => <option key={s.key} value={s.key}>{KIND_LABEL[s.kind]}{s.kind === 'commercial' || s.kind === 'general' ? '' : ` · ${s.label}`}</option>)}
        </select>
        <div role="radiogroup" aria-label="Quién lo ve" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button type="button" role="radio" aria-checked={mode === 'internal'} className={`action-btn ${mode === 'internal' ? 'btn-primary' : ''}`} onClick={() => setMode('internal')}>Interno · solo tú</button>
          <button type="button" role="radio" aria-checked={mode === 'shared'} className={`action-btn ${mode === 'shared' ? 'btn-primary' : ''}`} onClick={() => setMode('shared')}>Compartido · lo ve en su espacio</button>
        </div>
        <textarea style={{ ...field, minHeight: 80 }} value={text} maxLength={4000} onChange={e => setText(e.target.value)} aria-label="Texto" placeholder={mode === 'shared' ? 'Lo que la persona va a leer en su espacio…' : 'Solo para ti…'} />
        <div><button type="button" className="action-btn btn-primary" disabled={busy || !text.trim() || !v.followUpsReady} onClick={save}>{mode === 'shared' ? 'Compartir avance' : 'Guardar nota interna'}</button></div>
        {msg && <div style={muted} role="status">{msg}</div>}
      </div>
    </section>
  );
}

export function HistoryBlock({ v, onDeleted }) {
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState('');
  const kinds = ['all', ...['coaching', 'diagnostic', 'learning', 'commercial'].filter(k => v.history.some(h => kindOf(h.service) === k)), 'note', 'shared_update'];
  const shown = v.history.filter(h => filter === 'all' || (filter === 'note' || filter === 'shared_update' ? h.kind === filter : kindOf(h.service) === filter));
  const label = (s) => v.services.find(x => x.key === s)?.label || KIND_LABEL[kindOf(s)];
  const del = async (h) => {
    if (!window.confirm(h.kind === 'shared_update' ? '¿Borrar este avance? Deja de verse en su espacio.' : '¿Borrar esta nota interna?')) return;
    setError('');
    try { await coachCall({ action: 'deleteFollowUp', personId: v.person.id, noteId: h.id, confirm: true }); onDeleted(); } catch (e) { setError(errText(e)); }
  };
  return (
    <section style={card} aria-labelledby="hist-title">
      <h3 id="hist-title" style={h3}>Historial</h3>
      <div role="group" aria-label="Filtrar historial" style={{ display: 'flex', gap: 4, flexWrap: 'wrap', margin: '8px 0 10px' }}>
        {kinds.map(k => (
          <button key={k} type="button" aria-pressed={filter === k} onClick={() => setFilter(k)}
            style={{ border: `1px solid ${filter === k ? 'var(--globant-green)' : 'var(--globant-border)'}`, background: filter === k ? 'var(--teal-dim)' : '#FFFFFF', color: filter === k ? '#155C54' : 'var(--globant-text-secondary)', borderRadius: 999, padding: '0 12px', minHeight: 34, font: 'inherit', fontSize: 13, cursor: 'pointer' }}>
            {k === 'all' ? 'Todo' : k === 'note' ? 'Internos' : k === 'shared_update' ? 'Compartidos' : KIND_LABEL[k]}
          </button>
        ))}
      </div>
      {error && <div style={{ ...muted, color: '#9A6212' }}>{error}</div>}
      {!shown.length ? <div style={muted}>Sin registros.</div> : (
        <div style={{ display: 'grid', gap: 6 }}>
          {shown.slice(0, 80).map((h, i) => (
            <div key={h.id || i} style={{ background: h.kind === 'note' ? '#FBF8F1' : h.kind === 'shared_update' ? '#F1F8F6' : 'transparent', borderRadius: 10, padding: h.text ? '8px 10px' : '2px 0', fontSize: 14 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
                <span style={{ ...muted, minWidth: 92 }}>{fmtDate(h.at)}</span>
                <b>{h.title}</b>
                <span style={muted}>{label(h.service)}</span>
                {(h.kind === 'note' || h.kind === 'shared_update') && <button type="button" className="action-btn" style={{ fontSize: 11, padding: '0 8px', marginLeft: 'auto' }} onClick={() => del(h)}>Borrar</button>}
              </div>
              {h.text && <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{h.text}</div>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
